import { DownloadIcon, FileTextIcon, Loader2Icon, SheetIcon } from 'components/icons';
import {
    Attachment,
    AttachmentActions,
    AttachmentContent,
    AttachmentDescription,
    AttachmentMedia,
    AttachmentTitle,
} from 'components/ui/attachment';
import { Button } from 'components/ui/button';
import React from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { cn } from 'utils/cn';
import { NEUTRAL_TILE_CLASS } from 'utils/families';
import { FORMAT_NAMES, type TableFileFormat } from '../../utils/tableExport';

interface Props {
    format: TableFileFormat;
    fileName: string;
    /** The table's rows; `null` while a Figure's rows are still being read, when the card waits for them. */
    rows: number | null;
    /** The format the table's file is being generated in, from this card or from the table's own Download. */
    generating: TableFileFormat | null;
    onDownload: () => void;
}

/** The file an answer to a request for a file offers: its table, in the format asked. The answer's one amber action. */
const TableFileCard: React.FC<Props> = ({ format, fileName, rows, generating, onDownload }) => {
    const intl = useIntl();
    const busy = generating ?? (rows === null ? format : null);
    const generatingLabel = busy
        ? intl.formatMessage({ id: 'ai-chat.table.generating' }, { format: FORMAT_NAMES[busy] })
        : null;

    return (
        // Focus rings Download alone, by the keyboard only: the kit would ring the whole card on a click too.
        <Attachment
            state={busy ? 'processing' : 'done'}
            className="border-border bg-card new:border-0 new:shadow-card new:outline-hidden @container w-full max-w-[520px] flex-nowrap gap-3 rounded-xl focus-within:ring-0 has-data-[slot=attachment-content]:p-3 has-data-[slot=attachment-media]:p-3"
        >
            <AttachmentMedia
                className={cn(
                    'size-10 rounded-[10px]',
                    format === 'xlsx' ? 'bg-primary-wash text-primary-ink' : NEUTRAL_TILE_CLASS
                )}
            >
                {format === 'xlsx' ? <SheetIcon className="size-5" /> : <FileTextIcon className="size-5" />}
            </AttachmentMedia>
            <AttachmentContent>
                <AttachmentTitle className="text-meta text-foreground font-semibold">
                    {/* The name wraps rather than truncates: it is the file the user is about to save. */}
                    <span className="block wrap-anywhere whitespace-normal">{fileName}</span>
                </AttachmentTitle>
                <AttachmentDescription className="text-caption text-foreground-tertiary">
                    {generatingLabel ?? (
                        <FormattedMessage
                            id="ai-chat.file-card.meta"
                            values={{ format: FORMAT_NAMES[format], count: rows ?? 0 }}
                        />
                    )}
                </AttachmentDescription>
            </AttachmentContent>
            <AttachmentActions>
                <Button
                    variant="secondary"
                    size="icon-sm"
                    className={cn(
                        'text-meta aria-disabled:hover:bg-secondary gap-1.5 rounded-lg font-semibold aria-disabled:cursor-default',
                        !busy && '@min-[360px]:w-auto @min-[360px]:ps-2 @min-[360px]:pe-3'
                    )}
                    aria-label={
                        generatingLabel ?? intl.formatMessage({ id: 'ai-chat.file-card.download-named' }, { fileName })
                    }
                    disabled={busy !== null}
                    focusableWhenDisabled
                    onClick={onDownload}
                >
                    {busy ? (
                        <Loader2Icon className="size-3.5 motion-safe:animate-spin motion-reduce:animate-pulse" />
                    ) : (
                        <>
                            <DownloadIcon className="size-4" />
                            <span className="hidden @min-[360px]:inline">
                                <FormattedMessage id="ai-chat.table.download" />
                            </span>
                        </>
                    )}
                </Button>
            </AttachmentActions>
        </Attachment>
    );
};

export default TableFileCard;
